import {
  BasicAssumption,
  BasicPaymentType,
  NonCashPaymentType,
  SinglePaymentType,
  RepeatingAssumption,
  ReportAssumption,
  ReportPaymentType,
  UnitAssumption,
  FormulaAssumption
} from '@futrli-sdk/predict';
import React, { FC, useContext, useReducer, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { toastError } from '@futrli/ui-components';

import { deleteAssumption } from '../../../../core/api/assumption';
import {
  SIZES,
  PREDICTION_ACTION,
  ASSUMPTION_TYPES,
  AssumptionTypesUnion,
  ASSUMPTION_TYPES_URL_PARAMS
} from '../../../../core/constants';
import { AnyAction } from '../../../../core/types';
import { Spinner } from '../../../../ui/spinner/spinner';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { isReportAssumption } from '../data-grid/format-rows';
import { UserGridSettingsContext } from '../user-grid-settings/user-grid-settings-context';
import { ModalName } from '../assumptions/settings/modal-name';
import { updatePredictionAndPaymentType, extractIdsAndTypes } from '../assumptions/settings/settings-modal-utils';

import styles from '../../../../ui/spinner/spinner.scss';
import { EditMenu } from '../assumptions/settings/edit-menu';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { DeletePredictionDialog } from '../../../../forecast/prediction-builder/components/delete-prediction/delete-prediction';

export type AllowedAssumptionTypes = BasicAssumption | RepeatingAssumption | UnitAssumption | FormulaAssumption;
export type AllowedPaymentTypes = BasicPaymentType | SinglePaymentType | NonCashPaymentType;

interface Props {
  row: ReportAssumption | ReportPaymentType;
  accountName: string | JSX.Element;
  predictionCount: number;
  classes?: string;
  rowHeight: SIZES;
  onManualPredictionDrawerOpen: (predictionId?: string, predictionType?: string, isQuickCopy?: boolean) => void;
}

interface State {
  isDeleting: boolean;
  isRenaming: boolean;
  action: PREDICTION_ACTION | null;
}

enum ActionType {
  IS_DELETING = 'IS_DELETING',
  SET_STATE = 'SET_STATE',
  CLEAR_STATE = 'CLEAR_STATE',
  IS_RENAMING = 'IS_RENAMING'
}

export const reducer = (state: State, action: AnyAction) => {
  switch (action.type) {
    case ActionType.IS_DELETING:
      return Object.assign({}, state, { isDeleting: true });
    case ActionType.IS_RENAMING:
      return Object.assign({}, state, { isRenaming: true });
    case ActionType.SET_STATE:
      return Object.assign({}, state, { action: action.action, isLoading: false });
    case ActionType.CLEAR_STATE:
      return Object.assign({}, state, { action: null, isLoading: false, isDeleting: false, isRenaming: false });
    default:
      return state;
  }
};

export const Actions: FC<Props> = ({ row, predictionCount, rowHeight, onManualPredictionDrawerOpen }) => {
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const organisationId = tempOrganisationId || '';
  const workspaceId = tempWorkspaceId || '';
  const {
    forecast,
    fetchForecast,
    fetchForecastReport,
    isConsolidation,
    updateAssumption,
    deleteAssumption: deleteConsolidatedAssumption
  } = usePredictionsInfoContext();
  const { toggleExpandedRows, deactivateEditingMode } = useContext(UserGridSettingsContext);
  const { addErrorMessage } = useContext(PredictManagementContext);
  const [type, id, name] = useCallback((): [AssumptionTypesUnion, string, string] => {
    if (isReportAssumption(row)) {
      return [row.type, row.index, row.name];
    }
    return [row.assumption_type as ASSUMPTION_TYPES, row.assumption_id, row.assumption_name];
  }, [row])();
  const [{ isDeleting, action, isRenaming }, dispatch] = useReducer(reducer, {
    isDeleting: false,
    isRenaming: false,
    action: null
  });

  const deletePrediction = async () => {
    dispatch({ type: ActionType.IS_DELETING });

    if (isConsolidation) {
      try {
        await deleteConsolidatedAssumption?.(workspaceId!, organisationId, forecast!.id, id);
        if (predictionCount && predictionCount === 1) {
          toggleExpandedRows(row.parent_row_id);
        }
      } catch {
        toastError();
      } finally {
        clearState();
      }
    } else {
      try {
        await deleteAssumption(workspaceId!, organisationId, forecast!.id, type, id);

        // Is not allowed to delete a prediction if the editing mode is enabled,
        // but this ensures that the storage is always consistent
        deactivateEditingMode(forecast!.id, row.parent_row_id, id);

        await fetchForecastReport!('all');
        await fetchForecast!();

        if (predictionCount && predictionCount === 1) {
          toggleExpandedRows(row.parent_row_id);
        }
      } catch (error) {
        addErrorMessage!(error as Error);
      } finally {
        clearState();
      }
    }
  };

  const onSettingsSuccess = () => {
    clearState();
  };

  const renamePrediction = async (name: string) => {
    const [predictionType, predictionId, paymentType] = extractIdsAndTypes(row);

    if (!workspaceId || !organisationId || !forecast?.id) {
      return addErrorMessage?.(new Error('Missing params for updating row.'));
    }

    dispatch({ type: ActionType.IS_RENAMING });
    if (isConsolidation) {
      await updateAssumption?.({ name }, workspaceId, organisationId, forecast.id, predictionId);
      onSettingsSuccess();
      return;
    }

    updatePredictionAndPaymentType(
      workspaceId,
      organisationId,
      forecast.id,
      predictionType,
      predictionId,
      paymentType,
      {
        prediction: {
          ...row,
          name
        }
      },
      fetchForecastReport!,
      addErrorMessage!,
      fetchForecast!,
      onSettingsSuccess,
      onSettingsSuccess
    );
  };

  const onSelectMenuOption = (newAction: PREDICTION_ACTION) => {
    if (newAction === PREDICTION_ACTION.CONFIGURE) {
      const [predictionType, predictionId] = extractIdsAndTypes(row);
      onManualPredictionDrawerOpen(predictionId, ASSUMPTION_TYPES_URL_PARAMS[predictionType], false);

      return;
    }

    if (newAction === PREDICTION_ACTION.QUICK_COPY) {
      const [predictionType, predictionId] = extractIdsAndTypes(row);
      onManualPredictionDrawerOpen(predictionId, ASSUMPTION_TYPES_URL_PARAMS[predictionType], true);
      return;
    }

    dispatch({ type: ActionType.SET_STATE, action: newAction });
  };
  const clearState = () => dispatch({ type: ActionType.CLEAR_STATE });

  if (isDeleting) {
    return <Spinner size={rowHeight} className={styles.indent} />;
  }

  return (
    <div>
      <EditMenu onSelect={onSelectMenuOption} />

      {action === PREDICTION_ACTION.RENAME && (
        <ModalName name={name} onClose={clearState} onSuccess={renamePrediction} type={type} isRenaming={isRenaming} />
      )}

      {action === PREDICTION_ACTION.DELETE && (
        <DeletePredictionDialog
          isOpenDeleteDialog={true}
          handleToggleDeleteDialog={clearState}
          onSubmit={deletePrediction}
        />
      )}
    </div>
  );
};
